import React, { useState, useRef, useEffect } from 'react';
import { useBookSearch } from '../hooks/useBookSearch';
import BookAnchorCard from './BookAnchorCard';
import FlameVideo from './FlameVideo';
import { GENRE_TAGS, VIBE_TAGS } from '../data/seedTags';
import './InputView.css';

const MAX_BOOKS = 3;
const MAX_VIBES = 3;

const PHRASES = [
  "Your TBR needs standards.",
  "Find a book worth ghosting everyone for.",
  "A better book crush awaits.",
  "Ditch the DNFs.",
  "Meet your next favorite read.",
  "No more literary situationships.",
  "Your next chapter deserves better.",
  "Your next emotional support book awaits.",
  "Find a story worthy of your lore.",
];

function TypewriterHeading() {
  const [displayed, setDisplayed] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [erasing, setErasing] = useState(false);

  useEffect(() => {
    const phrase = PHRASES[phraseIdx];
    if (!erasing) {
      if (displayed.length < phrase.length) {
        const t = setTimeout(() => setDisplayed(phrase.slice(0, displayed.length + 1)), 90);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => setErasing(true), 3200);
      return () => clearTimeout(t);
    } else {
      if (displayed.length > 0) {
        const t = setTimeout(() => setDisplayed(d => d.slice(0, -1)), 48);
        return () => clearTimeout(t);
      }
      const t = setTimeout(() => {
        setPhraseIdx(i => (i + 1) % PHRASES.length);
        setErasing(false);
      }, 550);
      return () => clearTimeout(t);
    }
  }, [displayed, phraseIdx, erasing]);

  return (
    <h1 className="input-view__heading">
      {displayed}
      <span className="typewriter-cursor" aria-hidden="true" />
    </h1>
  );
}

export default function InputView({ onSubmit, myList = [], onNavigateToList }) {
  const [selectedVibes, setSelectedVibes] = useState([]);
  const [anchorBooks, setAnchorBooks]     = useState([]);

  const [inputValue, setInputValue]     = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex]   = useState(-1);
  const containerRef = useRef(null);
  const inputRef     = useRef(null);

  const { results, loading } = useBookSearch(inputValue);
  const showDropdown = dropdownOpen && (loading || results.length > 0 || inputValue.trim().length >= 2);

  function toggleVibe(vibe) {
    if (selectedVibes.includes(vibe)) {
      setSelectedVibes(v => v.filter(t => t !== vibe));
    } else if (selectedVibes.length < MAX_VIBES) {
      setSelectedVibes(v => [...v, vibe]);
    }
  }

  function addBook(book) {
    if (anchorBooks.length >= MAX_BOOKS) return;
    if (anchorBooks.find(b => b.key === book.key)) return;
    setAnchorBooks(prev => [...prev, book]);
    setInputValue('');
    setDropdownOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  }

  function addManually() {
    const trimmed = inputValue.trim();
    if (!trimmed || anchorBooks.length >= MAX_BOOKS) return;
    addBook({
      key: `manual:${trimmed}:${Date.now()}`,
      title: trimmed,
      author: '',
      coverId: null,
      coverUrl: null,
      year: null,
      source: 'manual',
    });
  }

  function handleKeyDown(e) {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, results.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter' && activeIndex === results.length) {
      e.preventDefault();
      addManually();
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      addBook(results[activeIndex]);
    } else if (e.key === 'Escape') {
      setDropdownOpen(false);
      setActiveIndex(-1);
    }
  }

  useEffect(() => {
    function handleClick(e) {
      if (!containerRef.current?.contains(e.target)) setDropdownOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => { setActiveIndex(-1); }, [results]);

  const canSubmit = selectedVibes.length > 0 || anchorBooks.length > 0;

  function handleSubmit() {
    onSubmit({ category: null, vibes: selectedVibes, anchorBooks });
  }

  return (
    <div className="input-view">

      {/* ── Full-width topbar ── */}
      <header className="input-topbar">
        <div className="input-topbar__brand">
          <FlameVideo height={32} />
          <img src="/matchbook-logo.svg" alt="Matchbook" className="input-topbar__logo" />
        </div>
        {onNavigateToList && (
          <button className="input-topbar__mylist" onClick={onNavigateToList}>
            <i className="ph ph-bookmarks" />
            My List
            {myList.length > 0 && (
              <span className="input-topbar__badge">{myList.length}</span>
            )}
          </button>
        )}
      </header>

      {/* ── Two-column body ── */}
      <div className="input-body">

        {/* Left: headline + sprites */}
        <div className="input-body__left">
          <TypewriterHeading />

          <div className="sprite-layer" aria-hidden="true">
            {[
              { cls: 'princess', src: '/sprite-princess.mp4',  w: 360 },
              { cls: 'couple',   src: '/sprite-couple.mp4',    w: 110 },
              { cls: 'sailor',   src: '/sprite-sailor.mp4',    w: 96  },
              { cls: 'detective',src: '/sprite-detective.mp4', w: 100 },
              { cls: 'witch',    src: '/sprite-witch.mp4',     w: 200 },
              { cls: 'whale',    src: '/sprite-whale.mp4',     w: 180 },
              { cls: 'bookgirl', src: '/sprite-bookgirl.mp4',  w: 104 },
              { cls: 'ufo',      src: '/sprite-ufo.mp4',       w: 112 },
              { cls: 'woman',    src: '/sprite-woman.mp4',     w: 96  },
            ].map(({ cls, src, w }) => (
              <div key={cls} className={`sprite-wrap sprite-wrap--${cls}`}>
                <video className="sprite-video" src={src} style={{ width: w }} autoPlay loop muted playsInline />
                <div className="sprite-sparkles">
                  <span className="dot dot--lime  dot--a" />
                  <span className="dot dot--beige dot--b" />
                  <span className="dot dot--lime  dot--c" />
                  <span className="dot dot--beige dot--d" />
                  <span className="dot dot--lime  dot--e" />
                  <span className="dot dot--beige dot--f" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right: form panel */}
        <div className="input-body__right">

          {/* Tags — genre/form + vibe/texture, one shared 3-pick limit */}
          <section className="input-section">
            <p className="input-section__label">
              Pick up to 3 tags
              {selectedVibes.length > 0 && (
                <span className="input-section__count">{selectedVibes.length} / {MAX_VIBES}</span>
              )}
            </p>

            <div className="input-section__pills input-section__pills--genre">
              {GENRE_TAGS.map(tag => {
                const selected = selectedVibes.includes(tag);
                const disabled = !selected && selectedVibes.length >= MAX_VIBES;
                return (
                  <button
                    key={tag}
                    className={`choice-pill${selected ? ' choice-pill--selected' : ''}${disabled ? ' choice-pill--disabled' : ''}`}
                    onClick={() => toggleVibe(tag)}
                    disabled={disabled}
                  >
                    {selected && <i className="ph ph-check" />}
                    {tag}
                  </button>
                );
              })}
            </div>

            <div className="input-section__pills input-section__pills--vibe">
              {VIBE_TAGS.map(tag => {
                const selected = selectedVibes.includes(tag);
                const disabled = !selected && selectedVibes.length >= MAX_VIBES;
                return (
                  <button
                    key={tag}
                    className={`choice-pill${selected ? ' choice-pill--selected' : ''}${disabled ? ' choice-pill--disabled' : ''}`}
                    onClick={() => toggleVibe(tag)}
                    disabled={disabled}
                  >
                    {selected && <i className="ph ph-check" />}
                    {tag}
                  </button>
                );
              })}
            </div>
          </section>

          <div className="input-section-divider" />

          {/* Anchor books */}
          <section className="input-section">
            <p className="input-section__label">
              Books you've loved
              <span className="input-section__sublabel"> — optional, but they help</span>
              {anchorBooks.length > 0 && (
                <span className="input-section__count">{anchorBooks.length} / {MAX_BOOKS}</span>
              )}
            </p>

            {anchorBooks.length > 0 && (
              <div className="anchor-books">
                {anchorBooks.map(book => (
                  <BookAnchorCard
                    key={book.key}
                    book={book}
                    onRemove={() => setAnchorBooks(prev => prev.filter(b => b.key !== book.key))}
                  />
                ))}
              </div>
            )}

            {anchorBooks.length < MAX_BOOKS && (
              <div className="input-view__search-container" ref={containerRef}>
                <div className="input-view__form">
                  <input
                    ref={inputRef}
                    className="input-view__input"
                    type="text"
                    placeholder={anchorBooks.length === 0 ? 'Search for a book…' : 'Add another book…'}
                    value={inputValue}
                    onChange={e => { setInputValue(e.target.value); setDropdownOpen(true); }}
                    onFocus={() => inputValue.length >= 2 && setDropdownOpen(true)}
                    onKeyDown={handleKeyDown}
                    aria-label="Search for a book"
                    autoComplete="off"
                  />
                  {loading && <span className="input-view__spinner" aria-hidden="true" />}
                </div>

                {showDropdown && (
                  <ul className="input-view__dropdown" role="listbox">
                    {loading && results.length === 0 && (
                      <li className="dropdown-item dropdown-item--loading">Searching…</li>
                    )}
                    {results.map((book, i) => (
                      <li
                        key={book.key}
                        className={`dropdown-item${i === activeIndex ? ' dropdown-item--active' : ''}`}
                        role="option"
                        aria-selected={i === activeIndex}
                        onMouseDown={() => addBook(book)}
                        onMouseEnter={() => setActiveIndex(i)}
                      >
                        <div className="dropdown-item__cover">
                          {book.coverUrl
                            ? <img src={book.coverUrl} alt="" width={28} height={40} />
                            : <span className="dropdown-item__cover-placeholder"><i className="ph ph-book" /></span>
                          }
                        </div>
                        <div className="dropdown-item__info">
                          <span className="dropdown-item__title">{book.title}</span>
                          <span className="dropdown-item__author">{book.author}{book.year ? ` · ${book.year}` : ''}</span>
                        </div>
                        <i className="ph ph-plus dropdown-item__add" />
                      </li>
                    ))}
                    {!loading && inputValue.trim() && (
                      <li
                        className={`dropdown-item dropdown-item--manual${activeIndex === results.length ? ' dropdown-item--active' : ''}`}
                        role="option"
                        aria-selected={activeIndex === results.length}
                        onMouseDown={addManually}
                        onMouseEnter={() => setActiveIndex(results.length)}
                      >
                        <span className="dropdown-item__manual-icon"><i className="ph ph-pencil-simple" /></span>
                        <span className="dropdown-item__manual-label">
                          Add <strong>"{inputValue.trim()}"</strong> manually
                        </span>
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}

            {/* CTA — sits directly beneath the search field */}
            <button
              className="input-view__cta"
              disabled={!canSubmit}
              onClick={handleSubmit}
            >
              Find my next read
              <i className="ph ph-arrow-right" />
            </button>
          </section>

        </div>
      </div>
    </div>
  );
}
